import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PasswordConfirmationErrorMatcher } from 'src/app/cadastro/confirmar-cadastro-form/password-confirmation-error-matcher.model';
import { CustomValidators } from 'src/app/perfil/alterar-senha-perfil-page/custom-validator';
import { PerfilService } from 'src/app/perfil/perfil.service';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { GoogleAnalyticsService } from 'src/app/shared/services/google-analytics.service';
import { ToastService } from 'src/app/shared/toast';

type FieldsKeys = 'currentPassword' | 'newPassword' | 'confirmPassword';

export type PasswordChangeEventData = {
  password: string;
  currentPassword: string;
};

@Component({
  selector: 'app-password-change',
  templateUrl: './password-change.component.html',
  styleUrls: ['./password-change.component.scss']
})
export class PasswordChangeComponent implements OnInit {
  form: FormGroup;
  routerState?: { isValidationAllowed: boolean };
  matcher = new PasswordConfirmationErrorMatcher();
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  private readonly HTTP_BUSSINESS_ERROR = 422;
  bussinessErrorMessage: string | null = null;
  showValidation = false;

  @Input()
  isInternal = false;

  @Input()
  loading = false;

  @Output()
  onPasswordChange: EventEmitter<PasswordChangeEventData> = new EventEmitter<PasswordChangeEventData>()

  @Output()
  onCancel: EventEmitter<string> = new EventEmitter<string>()

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private googleAnalyticsService: GoogleAnalyticsService,
  ) {
  }

  ngOnInit(): void {
    this.routerState = this.router.getCurrentNavigation()?.extras?.state as { isValidationAllowed: boolean };
    this.form = this.formBuilder.group({
      currentPassword: ['', []],
      newPassword: ['', Validators.compose([
        Validators.required,
        // 2. check whether the entered password has a number
        CustomValidators.patternValidator(/\d/, { hasNumber: true }),
        // 3. check whether the entered password has upper case letter
        CustomValidators.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
        // 4. check whether the entered password has a lower-case letter
        CustomValidators.patternValidator(/[a-z]/, { hasSmallCase: true }),
        // 5. check whether the entered password has a special character
        CustomValidators.patternValidator(/[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, { hasSpecialCharacters: true }),
        Validators.minLength(8)
      ])],
      confirmPassword: ['', [Validators.required]]
    }, { validator: this.checkPasswords });

    if (this.isInternal) {
      this.form.controls.currentPassword.setValidators([Validators.required])
    }
  }

  async handleSaveNewPassword(e: Event): Promise<void> {
    this.googleAnalyticsService.pushActionEvent({
      event: 'click',
      detail: 'salvar',
      context: 'portal reneg:alterar senha'
    });
    this.onPasswordChange.emit({
      password: this.getFieldValue('newPassword'),
      currentPassword: this.getFieldValue('currentPassword') ?? null,
    });
  }

  checkPasswords(group: FormGroup): any {
    const { value: newPassword } = group.get('newPassword');
    const { value: confirmPassword } = group.get('confirmPassword');

    const isFieldValid = (confirmPassword === newPassword) || group.get('confirmPassword').hasError('required');

    return isFieldValid ? null : { passwordsDoNotMatch: true };
  }

  hasAnyError(field: FieldsKeys): boolean {
    return this.form.get(field).touched && !this.form.get(field).valid;
  }

  get passwordsNotMatch(): boolean {
    return this.form.hasError('passwordsDoNotMatch');
  }

  getFieldValue(field: FieldsKeys) {
    return this.form.get(field).value;
  }

  handleCancel() {
    this.googleAnalyticsService.pushActionEvent({
      event: 'click',
      detail: 'cancelar',
      context: 'portal reneg'
    });
    this.onCancel.emit();
  }

  getIconByErrorStatus(errorToTrack: string): { icon: string, color: string } {
    const iconsByStatus = {
      error: 'cancel',
      valid: 'check_circle',
      neutral: 'circle'
    };

    if (this.form.get('newPassword').touched && (this.form.get('newPassword').hasError('required') || this.form.get('newPassword').hasError(errorToTrack))) {
      return { icon: iconsByStatus.error, color: 'text-error' };
    }

    if (this.form.get('newPassword').dirty && !this.form.get('newPassword').hasError('required') && !this.form.get('newPassword').hasError(errorToTrack)) {
      return { icon: iconsByStatus.valid, color: 'text-valid' };
    }

    return { icon: iconsByStatus.neutral, color: 'text-neutral' };
  }

  handleShowNewPassword() {
    this.googleAnalyticsService.pushActionEvent({
      event: 'click',
      detail: 'senha: deixar senha visivel',
      context: 'portal reneg:alterar senha'
    });
    this.showNewPassword = !this.showNewPassword;
  }

  handleShowCurrentPassword() {
    this.googleAnalyticsService.pushActionEvent({
      event: 'click',
      detail: 'confirmar senha: deixar senha visivel',
      context: 'portal reneg:alterar senha'
    });
    this.showCurrentPassword = !this.showCurrentPassword;
  }

}
